import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { EventsService } from 'src/app/service/events.service';
import { CertificatePdfService } from 'src/app/service/certificate-pdf.service';
import { CertificatesService } from 'src/app/service/certificates.service';
import { User } from 'src/app/model/user';
import { Track } from 'src/app/model/track';
import { TranslateService } from '@ngx-translate/core';
import { CertificateTemplate, CertificateVars, certificateAllowedForStatus, collectUsedVars } from 'src/app/model/certificate-template';
import { Event } from 'src/app/model/event';
import { Submission } from 'src/app/model/paper';

/**
 * Certificado de APRESENTAÇÃO de artigo.
 *
 * A folha A4 em tela e o PDF vetorial são COMPARTILHADOS com o editor de
 * template do chair: `<app-certificate-sheet>` + `CertificatePdfService`.
 * Este componente só monta os DADOS (vars) — reaproveitando o fluxo do
 * paper-info: getSubmission (título + autores) → getEventByTrack (nome + id
 * do evento) → getEventCertificate (modelo) → getEventChairs (assinaturas).
 * O certificado EXIGE modelo do evento: sem modelo, a página mostra
 * "indisponível" (não há mais enunciado-padrão).
 */
@Component({
  selector: 'app-presentation-certificate',
  templateUrl: './presentation-certificate.component.html',
  styleUrls: ['./presentation-certificate.component.scss'],
})
export class PresentationCertificateComponent implements OnInit {
  loading = true;

  vars: CertificateVars = {
    event: '', eventAcronym: '', eventYear: '', eventDates: '', eventLocation: '',
    title: '', id: '', track: '', topics: '', submissionGroup: '', tpcGroup: '',
    authors: '', authorsNames: '', firstAuthor: '', chairs: '',
    session: '', sessionDate: '', room: '', submittedAt: '', date: '',
  };

  /** Template do evento (null = sem modelo → página "indisponível"). */
  template: CertificateTemplate | null = null;

  /** Regras de emissão não satisfeitas p/ o AUTOR (chair bypassa via rota /admin). */
  blockedByStatus = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private submissionsService: SubmissionsService,
    private eventsService: EventsService,
    private certificatePdf: CertificatePdfService,
    private certificatesService: CertificatesService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.vars.date = this.formatDate(new Date());

    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) { this.loading = false; return; }

    this.submissionsService.getSubmission(id, true).subscribe(submission => {
      this.vars.title = submission.title || '';
      this.vars.id = String(submission.id || '');
      this.vars.topics = (submission.topics || []).map(t => t.name).filter(Boolean).join(', ');
      this.vars.submittedAt = submission.submittedAt
        ? this.formatDate(submission.submittedAt.toDate()) : '';

      const sortedAuthors = (submission.authors || [])
        .slice()
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      this.vars.authors = sortedAuthors.map(a => this.formatPerson(a.user)).filter(Boolean).join(', ');
      this.vars.authorsNames = sortedAuthors
        .map(a => a.user && (a.user.name || '').trim()).filter(Boolean).join(', ');
      this.vars.firstAuthor = sortedAuthors.length ? this.formatPerson(sortedAuthors[0].user) : '';

      // Deriva o id da trilha igual ao paper-info (o model é `Track | number`).
      const trackId = submission.trackID
        ?? (submission.track instanceof Track ? submission.track.id : (submission.track as any));
      if (!trackId) { this.loading = false; return; }

      // Nome da trilha — não bloqueia a página (getTrack usa cache).
      if (submission.track instanceof Track && submission.track.name) {
        this.vars.track = submission.track.name;
      } else {
        this.eventsService.getTrack(trackId).subscribe(
          track => this.vars.track = (track && track.name) || '',
          () => this.vars.track = '');
      }

      this.eventsService.getEventByTrack(trackId).subscribe(event => {
        this.vars.event = (event && event.name) || '';
        this.vars.eventAcronym = (event && event.acronym) || '';
        this.vars.eventYear = event && event.year ? String(event.year) : '';
        if (!event || !event.id) { this.loading = false; return; }

        // O certificado EXIGE modelo do evento (regra de produto). Sem modelo (ou
        // erro), a página mostra "indisponível" — não há mais enunciado-padrão.
        // Com modelo: resolve as variáveis sob demanda (sessão/sala, grupos, datas/
        // local), carrega as assinaturas ({{chairs}}) e libera a página.
        this.certificatesService.getEventCertificate(event.id).subscribe(data => {
          this.template = (data && data.template) || null;
          if (!this.template) { this.loading = false; return; }

          // Regras de emissão: aplicam ao AUTOR; na rota /admin (chair) há
          // bypass — o chair é dono da regra e precisa pré-visualizar.
          const isChairRoute = this.router.url.startsWith('/admin');
          this.blockedByStatus = !isChairRoute
            && !certificateAllowedForStatus(this.template, submission.status as any);
          if (this.blockedByStatus) { this.loading = false; return; }

          this.fetchOnDemandVars(this.template, event, submission);

          this.eventsService.getEventChairs(event.id).subscribe(chairs => {
            this.vars.chairs = (chairs || [])
              .map(c => this.formatPerson(c.user))
              .filter(Boolean)
              .join(', ');
            this.loading = false;
          }, () => this.loading = false);
        }, () => { this.template = null; this.loading = false; });
      }, () => this.loading = false);
    }, () => this.loading = false);
  }

  /** Baixa o PDF A4 vetorial equivalente à folha. Download silencioso via Blob.
   *  Só alcançável com modelo (o botão vive dentro da folha, que só renderiza
   *  quando `template` existe). */
  async downloadPdf(): Promise<void> {
    if (!this.template) { return; }
    const bytes = await this.certificatePdf.generateFromTemplate(this.template, this.vars);
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = this.pdfFilename();
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /**
   * Variáveis de CUSTO EXTRA — só busca o que o template realmente usa:
   *  - {{submissionGroup}}/{{tpcGroup}}: ids no getSubmission → nomes via
   *    listas de grupos do evento;
   *  - {{session}}/{{sessionDate}}/{{room}}: id da sessão → programa do evento;
   *  - {{eventDates}}/{{eventLocation}}: eventInfo (usa o hidratado; senão
   *    busca o evento completo).
   * Tudo não-bloqueante: falha vira variável vazia, nunca quebra a página.
   */
  private fetchOnDemandVars(template: CertificateTemplate, event: Event, submission: Submission): void {
    const used = collectUsedVars(template);
    const idOf = (v: any): number | null =>
      typeof v === 'number' ? v : (v && typeof v === 'object' && v.id ? v.id : null);

    if (used.has('submissionGroup')) {
      const pg: any = submission.paperGroup;
      const pgId = idOf(pg);
      if (pg && pg.name) {
        this.vars.submissionGroup = pg.name;
      } else if (pgId) {
        this.eventsService.getEventPaperGroups(event.id).subscribe(groups => {
          const g = (groups || []).find(x => x.id === pgId);
          this.vars.submissionGroup = (g && g.name) || '';
        }, () => {});
      }
    }

    if (used.has('tpcGroup')) {
      const tg: any = submission.tpcGroup;
      const tgId = idOf(tg);
      if (tg && tg.name) {
        this.vars.tpcGroup = tg.name;
      } else if (tgId) {
        this.eventsService.getEventTPCGroups(event.id, 'minimum').subscribe(groups => {
          const g = (groups || []).find(x => x.id === tgId);
          this.vars.tpcGroup = (g && g.name) || '';
        }, () => {});
      }
    }

    if (used.has('session') || used.has('sessionDate') || used.has('room')) {
      const applySession = (s: any) => {
        if (!s) { return; }
        this.vars.session = s.title || '';
        this.vars.room = s.room || '';
        this.vars.sessionDate = s.start ? this.formatDate(s.start.toDate ? s.start.toDate() : new Date(s.start)) : '';
      };
      const sess: any = submission.session;
      const sessId = idOf(sess);
      if (sess && sess.title) {
        applySession(sess);
      } else if (sessId) {
        this.eventsService.getSessionsByEvent(event.id).subscribe(sessions => {
          applySession((sessions || []).find(s => s.id === sessId));
        }, () => {});
      }
    }

    if (used.has('eventDates') || used.has('eventLocation')) {
      const applyInfo = (info: any) => {
        if (!info) { return; }
        const begin = info.beginAt ? this.formatDate(info.beginAt.toDate ? info.beginAt.toDate() : new Date(info.beginAt)) : '';
        const end = info.endAt ? this.formatDate(info.endAt.toDate ? info.endAt.toDate() : new Date(info.endAt)) : '';
        this.vars.eventDates = begin && end && begin !== end ? `${begin} – ${end}` : (begin || end);
        this.vars.eventLocation = [info.city, info.country].filter(Boolean).join(', ');
      };
      if (event.eventInfo && (event.eventInfo.beginAt || event.eventInfo.city)) {
        applyInfo(event.eventInfo);
      } else {
        this.eventsService.getEvent(event.id).subscribe(
          full => applyInfo(full && full.eventInfo), () => {});
      }
    }
  }

  /** Data por extenso no idioma corrente (emissão e submissão). */
  private formatDate(d: Date): string {
    return d.toLocaleDateString(
      this.translate.currentLang === 'pt-BR' ? 'pt-BR' : 'en-US',
      { year: 'numeric', month: 'long', day: 'numeric' });
  }

  /** Nome do arquivo a partir do título (sem acentos/símbolos). */
  private pdfFilename(): string {
    const base = (this.vars.title || '').toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
    return 'certificado-' + (base || 'apresentacao') + '.pdf';
  }

  /** "Nome Completo (afiliação)" — afiliação = sigla quando existe, senão o nome. */
  private formatPerson(user: User): string {
    if (!user) { return ''; }
    const name = (user.name || '').trim();
    const aff = user.profile && user.profile.affiliation
      ? (user.profile.affiliation.acronym || user.profile.affiliation.name)
      : '';
    if (!name) { return ''; }
    return aff ? `${name} (${aff})` : name;
  }
}
